import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, CheckCircle, XCircle, Ban, UserCheck, LogIn, Trash2,
  Activity, FileText, Target, CheckSquare, AlertTriangle, Info,
  Users, Calendar,
} from 'lucide-react';
import { api } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { StatCard } from '@/components/shared/StatCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

interface UserDetail {
  id: string;
  full_name: string;
  email: string;
  app_role: string;
  avatar_url: string | null;
  approval_status: string;
  email_verified: boolean;
  last_login_at: string | null;
  created_at: string;
  deleted_at: string | null;
  workspaces: Array<{ id: string; name: string; role: string }>;
}

interface ErrorLog {
  id: string;
  error_type: string;
  message: string;
  severity: string;
  url: string | null;
  created_at: string;
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

function fmtDate(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function getInitials(name: string) { return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2); }

export function UserDetailPage() {
  const { id: userId } = useParams<{ id: string }>();
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Edit state
  const [editName, setEditName] = useState('');
  const [editDays, setEditDays] = useState<string[]>([]);
  const [editHours, setEditHours] = useState(8);
  const [editInit, setEditInit] = useState(false);

  const { data: user, isLoading } = useQuery<UserDetail>({
    queryKey: ['admin-user', userId],
    queryFn: () => api.get(`/admin/users/${userId}`).then(r => r.data.data),
    enabled: !!userId,
  });

  useEffect(() => {
    if (user && !editInit) {
      setEditName(user.full_name || '');
      setEditDays(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);
      setEditHours(8);
      setEditInit(true);
    }
  }, [user, editInit]);

  const { data: errorData } = useQuery<{ data: ErrorLog[] }>({
    queryKey: ['admin-user-errors', userId],
    queryFn: () => api.get('/admin/error-logs', { params: { user_id: userId, limit: 50 } }).then(r => r.data),
    enabled: !!userId,
  });
  const errorLogs = errorData?.data ?? [];
  const criticalCount = errorLogs.filter(e => e.severity === 'critical').length;
  const errorCount = errorLogs.filter(e => e.severity === 'error').length;
  const warningCount = errorLogs.filter(e => e.severity === 'warning').length;
  const infoCount = errorLogs.filter(e => e.severity === 'info').length;

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
    queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    queryClient.invalidateQueries({ queryKey: ['admin-pending-count'] });
  };

  const approveMut = useMutation({ mutationFn: () => api.post(`/admin/users/${userId}/approve`), onSuccess: () => { invalidateAll(); toast({ title: 'User approved' }); } });
  const blockMut = useMutation({ mutationFn: () => api.post(`/admin/users/${userId}/block`), onSuccess: () => { invalidateAll(); toast({ title: 'User blocked' }); } });
  const unblockMut = useMutation({ mutationFn: () => api.post(`/admin/users/${userId}/unblock`), onSuccess: () => { invalidateAll(); toast({ title: 'User activated' }); } });
  const deleteMut = useMutation({ mutationFn: () => api.delete(`/admin/users/${userId}`), onSuccess: () => { invalidateAll(); toast({ title: 'User deleted' }); } });
  const impersonateMut = useMutation({
    mutationFn: () => api.post('/admin/impersonate', { target_user_id: userId }),
    onSuccess: (res) => {
      const { token, session_id } = res.data.data;
      // Store admin session for restoration
      sessionStorage.setItem('impersonation_admin_token', localStorage.getItem('access_token') || '');
      sessionStorage.setItem('impersonation_session_id', session_id);
      // Store impersonation info for banner display
      localStorage.setItem('impersonation_admin', JSON.stringify({
        adminId: currentUser?.id,
        targetUserId: user?.id,
        targetUserName: user?.full_name || user?.email,
        targetUserEmail: user?.email,
        startedAt: new Date().toISOString(),
      }));
      // Switch to impersonated user token
      localStorage.setItem('access_token', token);
      toast({ title: 'Impersonation Active', description: 'You are now logged in as this user. Use the banner to return.' });
      window.location.href = '/dashboard';
    },
    onError: (err: any) => toast({ title: 'Impersonation Failed', description: err?.response?.data?.error?.message, variant: 'destructive' }),
  });

  if (isLoading || !user) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="flex gap-4"><Skeleton className="h-16 w-16 rounded-full" /><Skeleton className="h-12 w-64" /></div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const isCurrentUser = user.id === currentUser?.id;

  return (
    <div className="space-y-6">
      <Link to="/admin/users" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to Users
      </Link>

      {/* Header */}
      <div className="flex items-start gap-4">
        <Avatar className="h-16 w-16">
          <AvatarFallback className="text-lg">{getInitials(user.full_name || user.email)}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{user.full_name || 'Unnamed'}</h1>
          <p className="text-muted-foreground">{user.email}</p>
          <div className="flex gap-2 mt-2">
            <Badge variant="secondary">{user.app_role}</Badge>
            {user.approval_status === 'approved' && <Badge className="bg-green-500/20 text-green-600 border-0">Approved</Badge>}
            {user.approval_status === 'pending' && <Badge className="bg-yellow-500/20 text-yellow-600 border-0">Pending</Badge>}
            {user.approval_status === 'blocked' && <Badge variant="destructive">Blocked</Badge>}
          </div>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          {user.approval_status === 'pending' && (
            <Button size="sm" variant="outline" className="text-green-600 hover:bg-green-50" onClick={() => approveMut.mutate()}>
              <CheckCircle className="h-4 w-4 mr-1" /> Approve
            </Button>
          )}
          {user.approval_status === 'approved' && !isCurrentUser && (
            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => blockMut.mutate()}>
              <Ban className="h-4 w-4 mr-1" /> Block
            </Button>
          )}
          {user.approval_status === 'blocked' && (
            <Button size="sm" variant="outline" className="text-green-600 hover:bg-green-50" onClick={() => unblockMut.mutate()}>
              <UserCheck className="h-4 w-4 mr-1" /> Activate
            </Button>
          )}
          {user.approval_status === 'approved' && !isCurrentUser && user.app_role !== 'admin' && (
            <Button size="sm" variant="outline" className="text-blue-600 hover:bg-blue-50" onClick={() => impersonateMut.mutate()} disabled={impersonateMut.isPending}>
              <LogIn className="h-4 w-4 mr-1" /> Login as
            </Button>
          )}
          {!isCurrentUser && (
            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteMut.mutate()}>
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          )}
          <Link to={`/performance/${user.id}`}>
            <Button size="sm" variant="outline">View Dashboard</Button>
          </Link>
        </div>
      </div>

      {/* Stat Cards — matching old system: Tasks, Goals, Groups, Teams, Sprints, Audit Logs */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
        <StatCard icon={CheckSquare} label="Tasks" value={0} color="blue" />
        <StatCard icon={Target} label="Goals" value={0} color="green" />
        <StatCard icon={Activity} label="Groups" value={0} color="purple" />
        <StatCard icon={Users} label="Teams" value={0} color="orange" />
        <StatCard icon={Calendar} label="Sprints" value={0} color="yellow" />
        <StatCard icon={FileText} label="Audit Logs" value={0} color="muted" />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="workspaces">Workspaces</TabsTrigger>
          <TabsTrigger value="errors">Errors</TabsTrigger>
          <TabsTrigger value="edit">Edit Profile</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="mt-4 space-y-6">
          <Card className="p-6">
            <h3 className="font-semibold mb-4">Login & Authentication</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-y-4 gap-x-8">
              <div><p className="text-xs text-muted-foreground">Last Sign In</p><p className="text-sm font-medium">{fmtDate(user.last_login_at)}</p></div>
              <div><p className="text-xs text-muted-foreground">Account Created</p><p className="text-sm font-medium">{fmtDate(user.created_at)}</p></div>
              <div>
                <p className="text-xs text-muted-foreground">Email Confirmed</p>
                <p className="text-sm font-medium flex items-center gap-1">
                  {user.email_verified ? <><CheckCircle className="h-4 w-4 text-green-500" /> Yes</> : <><XCircle className="h-4 w-4 text-red-400" /> No</>}
                </p>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Workspaces */}
        <TabsContent value="workspaces" className="mt-4">
          <Card className="p-6">
            <h3 className="font-semibold mb-1">Linked Workspaces</h3>
            <p className="text-sm text-muted-foreground mb-4">Workspaces this user belongs to ({user.workspaces.length})</p>
            {user.workspaces.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No workspaces</p>
            ) : (
              <div className="space-y-3">
                {user.workspaces.map(ws => (
                  <div key={ws.id} className="flex items-center justify-between p-3 rounded-lg border">
                    <div>
                      <p className="font-medium">{ws.name}</p>
                      <p className="text-xs text-muted-foreground">Workspace</p>
                    </div>
                    <Badge variant="secondary">{ws.role}</Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Errors */}
        <TabsContent value="errors" className="mt-4 space-y-4">
          <Card className="p-6">
            <h3 className="font-semibold mb-1">Error Statistics</h3>
            <p className="text-sm text-muted-foreground mb-4">Error logs attributed to this user</p>
            <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
              <StatCard icon={XCircle} label="Critical" value={criticalCount} color="red" />
              <StatCard icon={AlertTriangle} label="Error" value={errorCount} color="orange" />
              <StatCard icon={AlertTriangle} label="Warning" value={warningCount} color="yellow" />
              <StatCard icon={Info} label="Info" value={infoCount} color="blue" />
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <span className="text-sm font-medium">Total Errors</span>
              <span className="text-sm font-bold">{errorLogs.length}</span>
            </div>
          </Card>
        </TabsContent>

        {/* Edit Profile */}
        <TabsContent value="edit" className="mt-4">
          <Card className="p-6 max-w-2xl">
            <p className="text-sm text-muted-foreground mb-6">Edit this user's profile details</p>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input value={editName} onChange={e => setEditName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Working Days</Label>
                <div className="grid grid-cols-2 gap-2">
                  {DAYS.map(day => (
                    <label key={day} className="flex items-center space-x-2 cursor-pointer">
                      <Checkbox checked={editDays.includes(day)} onCheckedChange={checked => setEditDays(prev => checked ? [...prev, day] : prev.filter(d => d !== day))} />
                      <span className="text-sm capitalize">{day}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Daily Hours</Label>
                <Input type="number" step="0.5" value={editHours} onChange={e => setEditHours(parseFloat(e.target.value) || 0)} />
              </div>
              <div className="text-sm text-muted-foreground">
                Weekly Capacity: <span className="font-medium">{(editHours * editDays.length).toFixed(1)} hours/week</span>
                <span className="ml-1">({editHours}h × {editDays.length} days)</span>
              </div>
              <Button onClick={() => toast({ title: 'Profile saved' })}>Save changes</Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
